import React, { useState, useEffect } from "react";
import {
  Loader2,
  Search,
  Filter,
  ChevronDown,
  ShoppingCart,
  X,
  Eye,
} from "lucide-react";
import KruozAPI from "../../KruozAPI";
import PropTypes from "prop-types";
import AssetDetailView from "./AssetDetailView";

const ShopStep = ({
  step,
  quotationTemplate,
  companyId,
  onAssetSelect,
  selectedAssets = [],
  onAvailableAssetsChange = () => {},
  errors = {},
}) => {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [categories, setCategories] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [confirmationDialog, setConfirmationDialog] = useState({
    show: false,
    asset: null,
  });
  const [detailView, setDetailView] = useState({
    show: false,
    asset: null,
  });
  const [visibleItems, setVisibleItems] = useState(3);
  // Check if the step has saved selected assets and initialize them
  useEffect(() => {
    if (step && step.selectedAssets && step.selectedAssets.length > 0) {
      // If we have assets stored in the ShopStep, ensure they are in selectedAssets
      const savedAssets = step.selectedAssets;
      console.log("Loading saved assets from ShopStep:", savedAssets);
    }
  }, [step]);

  useEffect(() => {
    const fetchAssets = async () => {
      if (!quotationTemplate?.wizardSteps) return;
      setLoading(true);

      try {
        const hireDurationStep = quotationTemplate.wizardSteps.find(
          (step) => step.id === "hireDuration"
        );

        const deliveryDate = hireDurationStep?.fields.find(
          (field) => field.id === "deliveryDate"
        )?.value;

        const collectionDate = hireDurationStep?.fields.find(
          (field) => field.id === "collectionDate"
        )?.value;

        if (!deliveryDate || !collectionDate) return;

        const propertyIds = new Set();
        quotationTemplate.wizardSteps.forEach((step) => {
          step.fields.forEach((field) => {
            if (field.value && field.options) {
              const selectedOption = field.options.find(
                (option) =>
                  option.label === field.value || option.value === field.value
              );
              if (selectedOption?.properties) {
                selectedOption.properties.forEach((propId) =>
                  propertyIds.add(propId.id)
                );
              }
            }
          });
        });

        if (propertyIds.size === 0) return;

        const response = await KruozAPI.get(
          "Assets/AssetForBookingByDateRangeAndProperties",
          {
            params: {
              startDate: deliveryDate,
              endDate: collectionDate,
              properties: Array.from(propertyIds).join(","),
              companyId: companyId,
            },
          }
        );

        // Process and group assets by parent
        let finalGroupAssets = [];
        let lastParentId = null;
        let currentAsset = null;

        response.data.forEach((element) => {
          if (element.IsAvailable) {
            element.AvailableCount = 1;
          } else {
            element.AvailableCount = 0;
          }
          element.Added = false;

          // Check if asset is already selected
          if (selectedAssets.some((selected) => selected.Id === element.Id)) {
            element.Added = true;
            element.IsAvailable = false;
            element.AvailableCount = 0;
          }

          if (currentAsset == null || lastParentId != element.ParentId) {
            currentAsset = { ...element };
            currentAsset.Items = [];
            currentAsset.Items.push({ ...element });
            currentAsset.NoOfItems = 0;
            finalGroupAssets.push(currentAsset);
          } else {
            if (element.IsAvailable && !element.Added) {
              currentAsset.AvailableCount =
                (currentAsset.AvailableCount || 0) + 1;
            }
            currentAsset.Items.push({ ...element });
          }
          lastParentId = element.ParentId;
        });

        // Sort by AvailableCount
        finalGroupAssets.sort((a, b) => {
          return (b.AvailableCount || 0) - (a.AvailableCount || 0);
        });

        // Get distance for pricing calculations
        const locationStep = quotationTemplate.wizardSteps.find(
          (step) => step.id === "location"
        );
        const distance =
          locationStep?.fields.find((field) => field.id === "Distance")
            ?.value || 0;

        // Fetch price for each asset with available items
        const assetsWithPricing = await Promise.all(
          finalGroupAssets.map(async (asset) => {
            // Only fetch pricing if the asset has available items
            if (asset.AvailableCount > 0) {
              try {
                // Find the first available item to get pricing
                const availableItem = asset.Items.find(
                  (item) => item.IsAvailable && !item.Added
                );

                if (availableItem) {
                  const priceResponse = await KruozAPI.get(
                    "Bookings/BookingAssetPriceAndTransport",
                    {
                      params: {
                        assetId: availableItem.Id,
                        startDate: deliveryDate,
                        endDate: collectionDate,
                        distance: distance,
                      },
                    }
                  );

                  return {
                    ...asset,
                    assetPrice: priceResponse.data.assetPrice,
                    gst: priceResponse.data.gst,
                    totalPrice:
                      parseFloat(priceResponse.data.assetPrice) +
                      parseFloat(priceResponse.data.gst),
                    transportPrice: priceResponse.data.transportPrice,
                    transportGST: priceResponse.data.transportGst,
                    units: priceResponse.data.units,
                    formattedPrice: `$${parseFloat(
                      priceResponse.data.assetPrice
                    ).toFixed(2)}`,
                    pricingCalculated: true,
                  };
                }
              } catch (error) {
                console.error(
                  `Error fetching price for asset ${asset.Id}:`,
                  error
                );
              }
            }
            return asset;
          })
        );

        setAssets(assetsWithPricing);

        // Update available assets for parent component
        const availableAssets = assetsWithPricing.filter(
          (asset) =>
            (asset.AvailableCount > 0 || asset.IsAvailable) &&
            !selectedAssets.some((selected) => selected.Id === asset.Id)
        );
        onAvailableAssetsChange(availableAssets);

        // Extract categories
        const uniqueCategories = [
          ...new Set(
            assetsWithPricing
              .map((asset) => asset.AssetTypeName)
              .filter(Boolean)
          ),
        ];
        setCategories(uniqueCategories);
      } catch (error) {
        console.error("Error fetching assets:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAssets();
  }, [quotationTemplate, companyId, selectedAssets]);

  const showMoreItems = (e) => {
    e.preventDefault();
    setVisibleItems((prev) => prev + 3);
  };

  const updateAssetAvailability = (assetId, parentAsset) => {
    setAssets((prevAssets) => {
      return prevAssets.map((asset) => {
        if (asset.Id === parentAsset.Id) {
          // Find the first available item and mark it as not available
          const updatedItems = asset.Items.map((item) => {
            if (item.Id === assetId) {
              return { ...item, IsAvailable: false, Added: true };
            }
            return item;
          });

          // Count remaining available items
          const availableCount = updatedItems.filter(
            (item) => item.IsAvailable && !item.Added
          ).length;

          return {
            ...asset,
            Items: updatedItems,
            AvailableCount: availableCount,
          };
        }
        return asset;
      });
    });
  };

  const handleAddToCart = async (parentAsset) => {
    // Show confirmation dialog instead of immediately processing
    setConfirmationDialog({
      show: true,
      asset: parentAsset,
    });
    // Close detail view if it's open
    if (detailView.show) {
      setDetailView({ show: false, asset: null });
    }
  };

  const handleConfirmAddToCart = async () => {
    const parentAsset = confirmationDialog.asset;
    setConfirmationDialog({ show: false, asset: null });

    try {
      // Find the first available item from the Items array
      const availableItem = parentAsset.Items.find(
        (item) => item.IsAvailable && !item.Added
      );

      if (!availableItem) {
        console.error("No available items to add");
        return;
      }

      const hireDurationStep = quotationTemplate.wizardSteps.find(
        (step) => step.id === "hireDuration"
      );

      const locationStep = quotationTemplate.wizardSteps.find(
        (step) => step.id === "location"
      );

      const deliveryDate = hireDurationStep?.fields.find(
        (field) => field.id === "deliveryDate"
      )?.value;

      const collectionDate = hireDurationStep?.fields.find(
        (field) => field.id === "collectionDate"
      )?.value;

      const distance = locationStep?.fields.find(
        (field) => field.id === "Distance"
      )?.value;

      const priceResponse = await KruozAPI.get(
        "Bookings/BookingAssetPriceAndTransport",
        {
          params: {
            assetId: availableItem.Id,
            startDate: deliveryDate,
            endDate: collectionDate,
            distance: distance || 0,
          },
        }
      );

      const assetWithPricing = {
        ...availableItem,
        Cost: priceResponse.data.assetPrice,
        GST: priceResponse.data.gst,
        TransportPrice: priceResponse.data.transportPrice,
        TransportGST: priceResponse.data.transportGst,
        Units: priceResponse.data.units,
        startDate: deliveryDate,
        endDate: collectionDate,
        ParentAsset: {
          Id: parentAsset.Id,
          Name: parentAsset.Name,
          Code: parentAsset.Code,
        },
      };

      // Update availability in the UI
      updateAssetAvailability(availableItem.Id, parentAsset);

      // Call the parent component's onAssetSelect with the selected item
      onAssetSelect(assetWithPricing);

      // Update available assets for parent component
      const updatedAvailableAssets = assets.filter(
        (asset) =>
          (asset.AvailableCount > 0 || asset.IsAvailable) &&
          !selectedAssets.some((selected) => selected.Id === asset.Id)
      );
      onAvailableAssetsChange(updatedAvailableAssets);
    } catch (error) {
      console.error("Error processing asset addition:", error);
    }
  };

  const handleCancelAddToCart = () => {
    setConfirmationDialog({ show: false, asset: null });
  };

  const filteredAssets = assets.filter((asset) => {
    const matchesSearch =
      asset.Name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      asset.Code.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory =
      selectedCategory === "all" || asset.AssetTypeName === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }
  return (
    <div className="space-y-6">
      {/* Error message display */}
      {errors.assetSelection && assets.length > 0 && (
        <p></p>
        // <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4" role="alert">
        //   <p className="font-bold">Please select at least one item to continue.</p>
        // </div>
      )}

      {/* Search and Filters */}
      <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
        <div className="relative flex-1 max-w-md">
          <input
            type="text"
            placeholder="Search assets..."
            className="w-full pl-4 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {categories.length > 0 && (
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setSelectedCategory("all")}
              className={`px-4 py-2 rounded-full text-sm ${
                selectedCategory === "all"
                  ? "bg-blue-500 text-white"
                  : "bg-white text-gray-600 border hover:bg-gray-50"
              }`}
            >
              All Categories
            </button>
            {categories.map((category) => (
              <button
                type="button"
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full text-sm ${
                  selectedCategory === category
                    ? "bg-blue-500 text-white"
                    : "bg-white text-gray-600 border hover:bg-gray-50"
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Asset Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredAssets.slice(0, visibleItems).map((asset) => (
          <div
            key={asset.Id}
            className="product-card-one w-full h-full bg-white relative group overflow-hidden"
            style={{ boxShadow: "0px 15px 64px 0px rgba(0, 0, 0, 0.05)" }}
          >
            <div
              className="product-card-img w-full h-[300px]"
              style={{
                backgroundImage: `url(${
                  process.env.REACT_APP_KRUOZ_IMAGE_BASE_URL +
                  (asset.ProfilePicture || "CartNoImage.png")
                })`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center",
                backgroundSize: "contain",
              }}
            >
              {/* Availability Badge */}
              <div className="product-type absolute right-[14px] top-[17px]">
                {asset.AvailableCount > 0 ? (
                  <span className="text-[9px] font-700 leading-none py-[6px] px-3 uppercase text-white rounded-full tracking-wider bg-[#19CC40]">
                    {asset.AvailableCount} Available
                  </span>
                ) : (
                  <span className="text-[9px] font-700 leading-none py-[6px] px-3 uppercase text-white rounded-full tracking-wider bg-[#C96262]">
                    Not Available
                  </span>
                )}
              </div>

              {/* Price Badge */}
              {asset.pricingCalculated && (
                <div className="absolute left-[14px] top-[17px]">
                  <span className="text-sm font-bold py-2 px-4 text-white rounded-lg bg-blue-600 shadow-md">
                    $
                    {(
                      parseFloat(asset.assetPrice || 0) +
                      parseFloat(asset.gst || 0) +
                      parseFloat(asset.transportPrice || 0) +
                      parseFloat(asset.transportGST || 0)
                    ).toFixed(2)}
                  </span>
                </div>
              )}
            </div>{" "}
            <div className="product-card-details px-[30px] pb-[30px] relative">
              {/* Actions buttons */}
              <div className="absolute w-full px-4 left-0 top-60 group-hover:top-[35px] transition-all duration-300 ease-in-out">
                <div className="flex gap-2 max-w-full mx-auto">
                  <button
                    type="button"
                    onClick={() => setDetailView({ show: true, asset: asset })}
                    className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-800 py-2 px-2 rounded-md transition-all duration-300 flex items-center justify-center gap-1 shadow-md hover:shadow-lg"
                  >
                    <Eye className="h-5 w-5" />
                    <span className="font-semibold">View</span>
                  </button>

                  {asset.AvailableCount > 0 && (
                    <button
                      type="button"
                      onClick={() => handleAddToCart(asset)}
                      className="flex-1 bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-md transition-all duration-300 flex items-center justify-center gap-2 shadow-md hover:shadow-lg"
                    >
                      <ShoppingCart className="h-5 w-5" />
                      <span className="font-semibold">Add</span>
                    </button>
                  )}
                </div>
              </div>
              <div className="mt-4">
                <h4 className="text-lg font-semibold text-gray-800 mb-2 line-clamp-2 group-hover:text-blue-600 transition-colors">
                  {asset.Name}
                </h4>
                <div className="flex items-center justify-between mt-3">
                  {asset.pricingCalculated && (
                    <div className="flex flex-col items-center text-center mx-auto">
                      <span className="text-lg font-bold text-blue-600">
                        {asset.formattedPrice}
                      </span>
                      <span className="text-xs text-gray-500">
                        {`+$${parseFloat(asset.gst || 0).toFixed(2)} GST`}
                      </span>
                      {parseFloat(asset.transportPrice || 0) > 0 && (
                        <>
                          <span className="text-xs text-gray-500">
                            {`+$${parseFloat(asset.transportPrice || 0).toFixed(
                              2
                            )} transport`}
                          </span>
                          <span className="text-xs text-gray-500">
                            {`+$${parseFloat(asset.transportGST || 0).toFixed(
                              2
                            )} transport GST`}
                          </span>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Show More Button */}
      {filteredAssets.length > visibleItems && (
        <div className="flex justify-center mt-8">
          <button
            onClick={showMoreItems}
            className="px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-full transition-colors duration-300 flex items-center gap-2"
          >
            Show More
            <ChevronDown className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Confirmation Dialog */}
      {confirmationDialog.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full m-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Confirm Add to Cart</h3>
              <button
                onClick={handleCancelAddToCart}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="mb-6">
              <p className="text-gray-600">
                Are you sure you want to add{" "}
                <span className="font-semibold">
                  {confirmationDialog.asset?.Name}
                </span>{" "}
                to your cart?
              </p>
              {confirmationDialog.asset?.pricingCalculated ? (
                <div className="mt-3 p-3 bg-gray-50 rounded-md">
                  <div className="flex justify-between mb-1">
                    <span className="text-sm text-gray-600">Price:</span>
                    <span className="text-sm font-medium">
                      {confirmationDialog.asset.formattedPrice}
                    </span>
                  </div>
                 
                  {confirmationDialog.asset.transportPrice > 0 && (
                    <div className="flex justify-between mb-1">
                      <span className="text-sm text-gray-600">Transport:</span>
                      <span className="text-sm">
                        $
                        {parseFloat(
                          confirmationDialog.asset.transportPrice
                        ).toFixed(2)}
                      </span>
                    </div>
                  )}
                   <div className="flex justify-between mb-1">
                    <span className="text-sm text-gray-600">GST:</span>
                    <span className="text-sm">
                      $
                      {(parseFloat(confirmationDialog.asset.gst || 0) + parseFloat(confirmationDialog.asset.transportGST || 0)).toFixed(2)}
                    </span>
                  </div>
                 
                  <div className="flex justify-between font-bold mt-2 pt-2 border-t border-gray-200">
                    <span className="text-gray-800">Total:</span>
                    <span className="text-blue-600">
                      $
                      {(
                        parseFloat(confirmationDialog.asset.assetPrice || 0) +
                        parseFloat(confirmationDialog.asset.gst || 0) +
                        parseFloat(
                          confirmationDialog.asset.transportPrice || 0
                        ) +
                        parseFloat(confirmationDialog.asset.transportGST || 0)
                      ).toFixed(2)}
                    </span>
                  </div>
                </div>
              ) : (
                confirmationDialog.asset?.UnitPriceText && (
                  <p className="mt-2 text-sm text-gray-500">
                    Price: {confirmationDialog.asset.UnitPriceText}
                  </p>
                )
              )}
            </div>

            <div className="flex justify-end space-x-4">
              <button
                onClick={handleCancelAddToCart}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmAddToCart}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* No Results Message */}
      {filteredAssets.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500 text-lg">
            No assets found matching your criteria
          </p>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      )}

      {/* Asset Detail View */}
      {detailView.show && (
        <AssetDetailView
          asset={detailView.asset}
          onClose={() => setDetailView({ show: false, asset: null })}
          onAddToCart={handleAddToCart}
        />
      )}
    </div>
  );
};

ShopStep.propTypes = {
  step: PropTypes.shape({
    id: PropTypes.string,
    fields: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        value: PropTypes.any,
      })
    ),
  }),
  quotationTemplate: PropTypes.shape({
    wizardSteps: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        fields: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
            value: PropTypes.any,
            options: PropTypes.arrayOf(
              PropTypes.shape({
                label: PropTypes.string.isRequired,
                properties: PropTypes.arrayOf(
                  PropTypes.shape({
                    id: PropTypes.string.isRequired,
                  })
                ),
              })
            ),
          })
        ),
      })
    ),
  }).isRequired,
  companyId: PropTypes.string.isRequired,
  onAssetSelect: PropTypes.func.isRequired,
  selectedAssets: PropTypes.arrayOf(
    PropTypes.shape({
      Id: PropTypes.string.isRequired,
      Name: PropTypes.string.isRequired,
      Code: PropTypes.string.isRequired,
      ProfilePicture: PropTypes.string,
      IsAvailable: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
      UnitPriceText: PropTypes.string,
      Items: PropTypes.array,
    })
  ),
  onAvailableAssetsChange: PropTypes.func,
  errors: PropTypes.object,
};

ShopStep.defaultProps = {
  selectedAssets: [],
  onAvailableAssetsChange: () => {},
};

export default ShopStep;
